/**
 * @fileoverview Template: the single screen the whole app lives on.
 * @module components/templates/StageTemplate
 *
 * @description
 * Peak-Maxxing is a one-screen app, so this template is not a page frame but
 * a *stage*: it fills whatever height is left under the header and centres
 * its content there. It keeps a scroll fallback for the rare viewport too
 * short to fit everything (a large accessibility zoom, a tiny landscape
 * phone) — the target is scroll-free, not scroll-proof.
 */

import type { ReactNode } from "react"

import "./StageTemplate.css"

/** Props accepted by {@link StageTemplate}. */
export interface StageTemplateProps {
	/** Screen heading, kept for context and assistive technology. */
	readonly title: string
	/** True to keep the heading for screen readers only. Defaults to false. */
	readonly hideTitle?: boolean
	/** Stage content. */
	readonly children: ReactNode
	/** Optional strip pinned under the stage, e.g. the action bar. */
	readonly footer?: ReactNode
}

/**
 * Full-height, centred stage.
 *
 * @param props - Title, content and optional footer strip.
 * @returns The rendered screen.
 */
export function StageTemplate({
	title,
	hideTitle = false,
	children,
	footer,
}: StageTemplateProps) {
	return (
		<main className="stage">
			<h1 className={hideTitle ? "visually-hidden" : "stage__title"}>
				{title}
			</h1>
			<div className="stage__content">{children}</div>
			{footer !== undefined && (
				<div className="stage__footer">{footer}</div>
			)}
		</main>
	)
}
